import { FC, useEffect, useMemo, useState } from 'react';

import StepHeader from '@components/quiz/shared/StepHeader';
import CtaButton from '@components/quiz/shared/CtaButton';
import RatingGauge from '@components/quiz/shared/RatingGauge';
import ReadOnlyPitch from '@components/manual-team-entry/read-only-pitch';
import type { TeamMember } from '@components/manual-team-entry/types';
import { useGytr } from '@components/quiz/GytrProvider';
import { fetchSquadPlayers } from '@lib/api/gytr-portraits';
import { resolveInsightLine } from '@lib/page-utils/Gytr/insight';
import type { Player } from '@api-service/players';
import type { SanityGytrRevealBlock } from '@lib/sanity/types';

type GytrRevealStepProps = SanityGytrRevealBlock & {
  onNext: () => void;
};

const PROBLEM_SIGNALS = new Set(['lowProjectedStarter', 'flaggedStarter']);

/**
 * Step 4: the rating reveal. Animated graded gauge, a rules-based insight
 * line naming the weakest true signal, then the read-only pitch (portraits
 * + captain armband). Portraits are resolved from the squad UUIDs.
 */
const GytrRevealStep: FC<GytrRevealStepProps> = ({
  header,
  gauge,
  insightLines,
  ctaText,
  onNext
}) => {
  const { state } = useGytr();
  const [fetchedPortraits, setFetchedPortraits] = useState<Map<string, Player>>(
    new Map()
  );

  // Prefer the portraits prefetched during processing; fall back to a fetch
  // here if they're not warm yet (e.g. deep-link).
  const portraits = state.playerPortraits ?? fetchedPortraits;

  const squadUuids = useMemo(
    () =>
      state.team
        .map((member) => member.player?.id)
        .filter((id): id is string => Boolean(id)),
    [state.team]
  );

  useEffect(() => {
    if (squadUuids.length === 0) return;
    if (state.playerPortraits) return;
    let cancelled = false;
    fetchSquadPlayers(squadUuids)
      .then((map) => {
        if (!cancelled) setFetchedPortraits(map);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [squadUuids, state.playerPortraits]);

  const pitchTeam: TeamMember[] = useMemo(
    () =>
      state.team.map((member, index) => ({
        playerId: index,
        player: member.player ? portraits.get(member.player.id) ?? null : null
      })),
    [state.team, portraits]
  );

  const captainId = useMemo(
    () => state.team.find((member) => member.player?.isCaptain)?.player?.id,
    [state.team]
  );

  // The insight callout is opt-in per Sanity document (defaults off).
  const insight = insightLines?.enabled
    ? resolveInsightLine(state.team, state.hasWeakStarter, insightLines)
    : null;

  const bands = gauge?.bands?.map((band) => ({
    maxValue: band.maxValue,
    colour: band.colour
  }));

  return (
    <section className="flex flex-col items-center px-4 pb-28 pt-6">
      <div className="flex w-full max-w-sm flex-col items-center">
        <RatingGauge
          value={state.teamRating}
          bands={bands}
          subtext={gauge?.subtext}
        />
        <StepHeader
          eyebrow={header?.eyebrow}
          accentHeading={header?.heading}
          subtext={header?.subtext}
          className="mt-6"
        />

        {insight && (
          <div
            className={`mt-5 w-full rounded-xl px-4 py-3 text-center font-body text-sm font-semibold ${
              PROBLEM_SIGNALS.has(insight.signal)
                ? 'bg-warning-50 text-warning-700'
                : 'bg-neutral-50 text-neutral-600'
            }`}
          >
            {insight.text}
          </div>
        )}
      </div>

      <ReadOnlyPitch team={pitchTeam} captainId={captainId} />

      {/* CTA anchored to the bottom of the viewport so it stays available
          however far the user scrolls (matches the paywall pattern). */}
      <div
        className="fixed inset-x-0 bottom-0 z-40 border-t border-neutral-200 bg-white px-6 pt-3 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]"
        style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
      >
        <div className="mx-auto w-full max-w-sm">
          <CtaButton onClick={onNext} dataCy="gytr-reveal-cta">
            {ctaText ?? 'Suggest transfers'}
          </CtaButton>
        </div>
      </div>
    </section>
  );
};

export default GytrRevealStep;
